import {
  useEffect,
  useState
} from 'react'
import {
  useNavigate
} from 'react-router-dom'
import type {
  AssistantCharacter
} from '../../services/api'
import {
  authService,
  automaticApprovalsService
} from '../../services/api'
import {
  useAuth
} from '../../hooks/useAuth'
import {
  CHANNEL_LABELS,
  clearPermissionsDeferred
} from '../../hooks/useOnboardingState'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'
import {
  PartyPopper, Settings, ExternalLink
} from 'lucide-react'
import type {
  ConnectedChannel
} from '../../hooks/useOnboardingState'

interface CompletionStepProps {
  character: AssistantCharacter | null
  connectedChannel: ConnectedChannel
  whatsappLink?: string | null | undefined
  slackDeepLink?: string | null | undefined
  slackWorkspaceName?: string | null | undefined
  smsPhoneNumber?: string | null | undefined
  imessageFromNumber?: string | null | undefined
}

function getDeepLink(
  channel: ConnectedChannel,
  whatsappLink?: string | null,
  slackDeepLink?: string | null,
  smsPhoneNumber?: string | null,
  imessageFromNumber?: string | null,
): string | null {
  if (channel === 'whatsapp' && whatsappLink) return whatsappLink
  if (channel === 'slack' && slackDeepLink) return slackDeepLink
  if (channel === 'sms' && smsPhoneNumber) return `sms:${smsPhoneNumber}`
  if (channel === 'imessage' && imessageFromNumber) return `sms:${imessageFromNumber}`
  return null
}

export function CompletionStep({
  character,
  connectedChannel,
  whatsappLink,
  slackDeepLink,
  slackWorkspaceName,
  smsPhoneNumber,
  imessageFromNumber,
}: CompletionStepProps): JSX.Element {
  const navigate = useNavigate()
  const { setAuthUser } = useAuth()

  // The only source of truth for whether this account actually has the mode:
  // the default is A/B'd per signup, enrollment is a fire-and-forget PostHog
  // write, and flag reads are cached ~30s. Ask, and say nothing until the
  // answer comes back true — silence is the honest default here, not an
  // assumption in either direction.
  const [approvalsOn, setApprovalsOn] = useState(false)

  useEffect(() => {
    let cancelled = false
    let retry: ReturnType<typeof setTimeout> | undefined

    // A `false` here is ambiguous in a way `true` isn't: it means either a
    // genuine holdout, or an enrolled user whose signup-time property write
    // hasn't reached the flag yet (PostHog ingest, plus the server's cached
    // read). One delayed re-ask recovers the second group — whose disclosure is
    // the whole reason GET-12229 put this card here — and tells the first group
    // nothing untrue, since it still resolves to false for them.
    //
    // The delay must clear `_FLAG_CACHE_TTL_SECONDS` (30s, per process) or the
    // retry hits that cache on the same pod and re-evaluates nothing, which is
    // what a shorter delay bought: a second request and the same answer.
    const RETRY_AFTER_FLAG_CACHE_MS = 35_000

    const ask = (isRetry: boolean): void => {
      void automaticApprovalsService
        .getStatus()
        .then((status) => {
          if (cancelled) return
          setApprovalsOn(status.enabled)
          if (!status.enabled && !isRetry) {
            retry = setTimeout(() => ask(true), RETRY_AFTER_FLAG_CACHE_MS)
          }
        })
        .catch(() => {
          if (cancelled) return
          setApprovalsOn(false)
          if (!isRetry) retry = setTimeout(() => ask(true), RETRY_AFTER_FLAG_CACHE_MS)
        })
    }

    ask(false)
    return () => {
      cancelled = true
      if (retry !== undefined) clearTimeout(retry)
    }
  }, [])

  useEffect(() => {
    trackEvent(OnboardingWizardEvents.COMPLETION_STEP_VIEWED, {
      character_id: character?.id,
      character_name: character?.name,
      channel: connectedChannel,
    })
    trackEvent(OnboardingWizardEvents.WIZARD_COMPLETED, {
      character_id: character?.id,
      channel: connectedChannel,
    })
    // Mark setup as complete on the backend and update local user
    void authService.completeSetup().then((updatedUser) => {
      setAuthUser(updatedUser)
      // This onboarding run is over, so retire its permissions deferral. The id
      // comes from the response rather than the captured auth user, so this
      // can't silently no-op on a null-at-mount user — the failure mode there
      // (a reactivated account skipping the permissions step) is the one this
      // call exists to prevent, and it would be invisible. Only on success: a
      // failed call means setup isn't complete and the flag still has a run to
      // cover.
      clearPermissionsDeferred(updatedUser.id)
    }).catch(() => {
      // Non-critical — user can still navigate away
    })
  }, [])

  const deepLink = getDeepLink(connectedChannel, whatsappLink, slackDeepLink, smsPhoneNumber, imessageFromNumber)
  const channelLabel = connectedChannel ? CHANNEL_LABELS[connectedChannel] : null

  const handleDeepLinkClick = (): void => {
    trackEvent(OnboardingWizardEvents.DEEP_LINK_CLICKED, {
      channel: connectedChannel,
    })
  }

  const handleGoToSettings = (): void => {
    trackEvent(OnboardingWizardEvents.GO_TO_SETTINGS_CLICKED)
    navigate('/integrations')
  }

  return (
    <div className="animate-scale-in text-center">
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 text-primary-600 mb-4 animate-pulse-once">
          <PartyPopper className="w-8 h-8" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900 mb-3">
          You're all set!
        </h2>
        <p className="text-gray-500 text-lg max-w-sm mx-auto">
          {character?.name ? `${character.name} is ready` : 'Your assistant is ready'} to help you manage your schedule
          {channelLabel ? ` on ${channelLabel}` : ''}.
        </p>
      </div>

      {character && (
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-primary-200 ring-offset-2">
            {character.picture_url ? (
              <img
                src={character.picture_url}
                alt={character.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center text-lg font-bold text-primary-700">
                {character.name.charAt(0)}
              </div>
            )}
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">{character.name}</p>
            {connectedChannel && (
              <p className="text-sm text-gray-500">
                Connected via {channelLabel}
                {slackWorkspaceName ? ` (${slackWorkspaceName})` : ''}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3 max-w-xs mx-auto">
        {deepLink ? (
          <a
            href={deepLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDeepLinkClick}
            className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96] shadow-soft hover:shadow-soft-lg"
          >
            Start chatting on {channelLabel}
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <button
            onClick={handleGoToSettings}
            className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96] shadow-soft hover:shadow-soft-lg"
          >
            {connectedChannel ? `Open ${channelLabel}` : 'Go to Settings'}
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={handleGoToSettings}
          className="flex items-center justify-center gap-2 w-full text-gray-500 hover:text-gray-700 px-6 py-2.5 rounded-xl font-medium transition-colors"
        >
          <Settings className="w-4 h-4" />
          Go to Settings
        </button>
      </div>

      {/* Automatic approvals are on from the start for most signups
          (GET-12229). A callout rather than a footnote, and placed here because
          this is the last screen before the assistant begins acting: someone
          should know how much it will do on their own say-so before it does it,
          not work it out afterwards. Reads as a setting, which is what it is.

          The whole card is gated on the backend's verified state, not just the
          badge. Accounts from the concluded `auto-approval-default-new-users`
          holdout arm genuinely do not have this — describing the behaviour to them and then not
          doing it is worse than saying nothing, and Settings still carries the
          switch either way.

          The effect above re-asks once past the server's flag-cache TTL, which
          is what recovers an enrolled user whose read hadn't caught up.
          Residual after that: if it still says off, we stay silent — the chosen
          direction, since silence beats asserting autonomy we can't confirm —
          and the Settings card is that user's backstop rather than this screen.
          Someone who leaves the screen before the retry lands is in the same
          position. */}
      {approvalsOn && (
        <div className="mt-8 max-w-sm mx-auto rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold text-gray-900 text-sm">Automatic approvals</p>
            <span className="inline-flex items-center rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
              On
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {character?.name ?? 'Your assistant'} gets on with the small, low-risk
            things you&rsquo;ve clearly asked for, without stopping to confirm each
            one. Anything risky or unclear still comes to you first.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Rather approve everything yourself? Turn this off in Settings, or just
            say the word.
          </p>
        </div>
      )}
    </div>
  )
}
